import {
    PermissionSourceDesignatorType,
    type PermissionEffect,
    type PermissionSourceDesignator,
    type PermissionsGrant,
    IpgSystemIdToName,
    IpwSystemIdToName,
    resourceDesignatorKey,
} from '@pillar-portal/api-types';
import { Badge } from '@/components/ui/badge';
import { AuthorizableAction, ResourceType } from '@pillar-portal/database/dist/frontend';
import { cn } from '@/lib/utils';

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
    [ResourceType.ORGANIZATION]: 'Organization',
    [ResourceType.USER]: 'User',
    [ResourceType.CREDENTIAL]: 'Credential',
    [ResourceType.ORGANIZATION_CREDENTIAL]: 'Organization Credential',
    [ResourceType.ROLE_ASSIGNMENT_CREDENTIAL]: 'Role Assignment Credential',
    [ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY]: 'Organization Website Credential Policy',
    [ResourceType.IPX_CREDENTIAL_DEFINITION]: 'Website Sign-In Definition',
    [ResourceType.IPX_WEB_RESOURCE_MODEL]: 'Website Map',
    [ResourceType.ALLOWED_WEBSITE_TEMPLATE]: 'Allowed Website Template',
    [ResourceType.PERMISSIONS_BUNDLE]: 'Permissions Bundle',
    [ResourceType.PERMISSION_POLICY]: 'Permission Policy',
    [ResourceType.ROLE_ASSIGNMENT]: 'Role Assignment',
    [ResourceType.RPA_MODULE]: 'RPA Module',
    [ResourceType.IPW]: 'Website',
    [ResourceType.IPG]: 'Payment Processor',
    [ResourceType.PROCESSOR]: 'Processor',
    [ResourceType.MERCHANT_ACCOUNT]: 'Merchant Account',
    [ResourceType.LOGICAL_TERMINAL]: 'Logical Terminal',
    [ResourceType.TERMINAL_DEVICE]: 'Terminal Device',
    [ResourceType.PAYMENT_TRANSACTION]: 'Payment Transaction',
    [ResourceType.PAYMENT_REQUEST]: 'Payment Request',
    [ResourceType.PAYMENT_METHOD_TOKEN]: 'Payment Method Token',
};

const RESOURCE_TYPE_PLURAL_LABELS: Record<ResourceType, string> = {
    [ResourceType.ORGANIZATION]: 'organizations',
    [ResourceType.USER]: 'users',
    [ResourceType.CREDENTIAL]: 'credentials',
    [ResourceType.ORGANIZATION_CREDENTIAL]: 'organization credentials',
    [ResourceType.ROLE_ASSIGNMENT_CREDENTIAL]: 'role assignment credentials',
    [ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY]: 'organization website credential policies',
    [ResourceType.IPX_CREDENTIAL_DEFINITION]: 'website sign-in definitions',
    [ResourceType.IPX_WEB_RESOURCE_MODEL]: 'Website Maps',
    [ResourceType.ALLOWED_WEBSITE_TEMPLATE]: 'allowed website templates',
    [ResourceType.PERMISSIONS_BUNDLE]: 'permissions bundles',
    [ResourceType.PERMISSION_POLICY]: 'permission policies',
    [ResourceType.ROLE_ASSIGNMENT]: 'role assignments',
    [ResourceType.RPA_MODULE]: 'RPA modules',
    [ResourceType.IPW]: 'websites',
    [ResourceType.IPG]: 'payment processors',
    [ResourceType.PROCESSOR]: 'processors',
    [ResourceType.MERCHANT_ACCOUNT]: 'merchant accounts',
    [ResourceType.LOGICAL_TERMINAL]: 'logical terminals',
    [ResourceType.TERMINAL_DEVICE]: 'terminal devices',
    [ResourceType.PAYMENT_TRANSACTION]: 'payment transactions',
    [ResourceType.PAYMENT_REQUEST]: 'payment requests',
    [ResourceType.PAYMENT_METHOD_TOKEN]: 'payment method tokens',
};

const AUTHORIZABLE_ACTION_VERB_LABELS: Record<AuthorizableAction, string> = {
    [AuthorizableAction.USE]: 'use',
    [AuthorizableAction.LIST]: 'list',
    [AuthorizableAction.DISPLAY]: 'display',
    [AuthorizableAction.CREATE]: 'create',
    [AuthorizableAction.EDIT]: 'edit',
    [AuthorizableAction.DELETE]: 'delete',
    [AuthorizableAction.GRANT]: 'grant',
    [AuthorizableAction.REVOKE]: 'revoke',
    [AuthorizableAction.ROTATE]: 'rotate',
    [AuthorizableAction.TAKE_PAYMENT]: 'take payment',
    [AuthorizableAction.VOID_PAYMENT]: 'void payment',
    [AuthorizableAction.REFUND_PAYMENT]: 'refund payment',
    [AuthorizableAction.OVERRIDE_FEE]: 'override fees',
    [AuthorizableAction.IMPERSONATE]: 'impersonate',
    [AuthorizableAction.AUDIT]: 'audit',
};

export interface PermissionGrantSummaryProps {
    permission: PermissionsGrant;
    compact?: boolean;
    organizationNameByUuid?: Readonly<Record<string, string>>;
    designatorLabelByKey?: Readonly<Record<string, string>>;
}

export function formatPermissionResourceTypeLabel(resourceType: ResourceType): string {
    return RESOURCE_TYPE_LABELS[resourceType];
}

export function formatPermissionResourceTypePluralLabel(resourceType: ResourceType): string {
    return RESOURCE_TYPE_PLURAL_LABELS[resourceType];
}

export function formatPermissionActionVerbList(actions: readonly AuthorizableAction[]): string {
    const actionVerbs: string[] = actions.map((action) => AUTHORIZABLE_ACTION_VERB_LABELS[action]);
    if (actionVerbs.length === 0) {
        return '';
    }
    if (actionVerbs.length === 1) {
        return actionVerbs[0] ?? '';
    }
    if (actionVerbs.length === 2) {
        return `${actionVerbs[0] ?? ''} and ${actionVerbs[1] ?? ''}`;
    }
    const leadingActionVerbs = actionVerbs.slice(0, -1);
    const trailingActionVerb = actionVerbs.at(-1) ?? '';
    return `${leadingActionVerbs.join(', ')}, and ${trailingActionVerb}`;
}

export function formatPermissionEffectLabel(effect: PermissionEffect): string {
    return effect === 'deny' ? 'Deny' : 'Allow';
}

export function formatPermissionDesignatorLabel(
    resource: PermissionSourceDesignator,
    targetResourceType?: ResourceType,
    organizationNameByUuid?: Readonly<Record<string, string>>,
    designatorLabelByKey?: Readonly<Record<string, string>>
): string {
    if (resource.designator_type === PermissionSourceDesignatorType.WILDCARD) {
        if (targetResourceType === undefined) {
            return 'All in-scope target resources';
        }
        return `All in-scope ${formatPermissionResourceTypeLabel(targetResourceType).toLowerCase()} resources`;
    }
    if (resource.designator_type === PermissionSourceDesignatorType.SELF) {
        return 'Current active role assignment';
    }
    const resourceDesignator = resource;
    const resolvedLabel: string | undefined =
        designatorLabelByKey?.[resourceDesignatorKey(resourceDesignator)];
    if (resolvedLabel !== undefined) {
        return resolvedLabel;
    }
    switch (resourceDesignator.designator_type) {
        case ResourceType.ORGANIZATION: {
            const organizationLabel =
                organizationNameByUuid?.[resourceDesignator.organization_uuid] ??
                resourceDesignator.organization_uuid;
            return resourceDesignator.descendants
                ? `Organization ${organizationLabel} and descendants`
                : `Organization ${organizationLabel}`;
        }
        case ResourceType.USER:
            return `User ${resourceDesignator.user_uuid}`;
        case ResourceType.ROLE_ASSIGNMENT:
            return `Role assignment ${resourceDesignator.role_assignment_uuid}`;
        case ResourceType.CREDENTIAL:
            return `Credential ${resourceDesignator.credential_uuid}`;
        case ResourceType.ORGANIZATION_IPX_CREDENTIAL_POLICY:
            return (
                'Organization website credential policy ' +
                resourceDesignator.organization_ipx_credential_policy_uuid
            );
        case ResourceType.IPX_CREDENTIAL_DEFINITION:
            return (
                'Website sign-in definition ' + resourceDesignator.ipx_credential_definition_uuid
            );
        case ResourceType.IPX_WEB_RESOURCE_MODEL:
            return 'Website Map ' + resourceDesignator.ipx_web_resource_model_uuid;
        case ResourceType.ALLOWED_WEBSITE_TEMPLATE:
            return 'Allowed website template ' + resourceDesignator.allowed_website_template_uuid;
        case ResourceType.IPW:
            return `Website ${IpwSystemIdToName[resourceDesignator.ipw_system_id]}`;
        case ResourceType.IPG:
            return `Payment processor ${IpgSystemIdToName[resourceDesignator.ipg_system_id]}`;
        case ResourceType.PROCESSOR:
            return `Processor ${resourceDesignator.processor_uuid}`;
        case ResourceType.MERCHANT_ACCOUNT:
            return `Merchant account ${resourceDesignator.merchant_account_uuid}`;
        case ResourceType.LOGICAL_TERMINAL:
            return `Logical terminal ${resourceDesignator.logical_terminal_uuid}`;
        case ResourceType.TERMINAL_DEVICE:
            return `Terminal device ${resourceDesignator.terminal_device_uuid}`;
        case ResourceType.PAYMENT_TRANSACTION:
            return `Payment transaction ${resourceDesignator.payment_transaction_uuid}`;
        case ResourceType.PAYMENT_REQUEST:
            return `Payment request ${resourceDesignator.payment_request_uuid}`;
        case ResourceType.PAYMENT_METHOD_TOKEN:
            return `Payment method token ${resourceDesignator.payment_method_token_uuid}`;
        case ResourceType.PERMISSIONS_BUNDLE:
            return `Permissions bundle ${resourceDesignator.permissions_bundle_uuid}`;
        case ResourceType.PERMISSION_POLICY:
            return `Permission policy ${resourceDesignator.permission_policy_uuid}`;
        case ResourceType.RPA_MODULE:
            return `RPA module ${resourceDesignator.rpa_module_name}`;
    }
}

export function PermissionGrantSummary({
    permission,
    compact = false,
    organizationNameByUuid,
    designatorLabelByKey,
}: PermissionGrantSummaryProps) {
    const containerClassName = compact ? 'space-y-1' : 'space-y-3';
    const resourceLabelClassName = compact
        ? 'text-xs bg-gray-100 px-1.5 py-0.5 rounded'
        : 'text-xs bg-gray-100 px-2 py-1 rounded';

    return (
        <div className={containerClassName}>
            <div className="text-sm">
                <span className="font-medium">Effect:</span>{' '}
                <span>{formatPermissionEffectLabel(permission.effect)}</span>
            </div>

            <div className="text-sm">
                <span className="font-medium">Resource type:</span>{' '}
                <span>{formatPermissionResourceTypeLabel(permission.resource_type)}</span>
            </div>

            <div>
                <span className="text-sm font-medium">Actions:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                    {permission.actions.map((action, index) => (
                        <Badge
                            key={`${permission.resource_type}-${action}-${index}`}
                            variant="outline"
                        >
                            {action}
                        </Badge>
                    ))}
                </div>
            </div>

            <div>
                <span className="text-sm font-medium">Sources:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                    {permission.resources.map((resource, index) => (
                        <span
                            key={`${index}-${formatPermissionDesignatorLabel(resource, permission.resource_type, organizationNameByUuid, designatorLabelByKey)}`}
                            className={cn(resourceLabelClassName)}
                        >
                            {formatPermissionDesignatorLabel(
                                resource,
                                permission.resource_type,
                                organizationNameByUuid,
                                designatorLabelByKey
                            )}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
